// Copyright (c) 2024-2026 Echonum LLC. All rights reserved.
// Product: Exploit Score ETS (Exploit Threat Score)
//
// This file is part of Exploit Score ETS, proprietary software developed by
// Echonum LLC. Unauthorized copying, distribution, modification, or use of
// this file, via any medium, is strictly prohibited without express written
// permission from Echonum LLC.
//
// Confidential and Proprietary.

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../api/client'
import { MessageSquare, Loader2, ChevronRight, ArrowUpRight, Building2 } from 'lucide-react'

interface Ticket {
  id: string; ticket_number: string; subject: string
  category: string; priority: string; status: string; escalated: boolean
  created_at: string | null; updated_at: string | null; comment_count: number
  customer: { email: string | null; company_name: string | null; contact_name: string | null }
}

const STATUS_STYLE: Record<string, string> = {
  open: 'bg-blue-500/15 text-blue-400',
  in_progress: 'bg-indigo-500/15 text-indigo-400',
  waiting_customer: 'bg-amber-500/15 text-amber-400',
  resolved: 'bg-green-500/15 text-green-400',
  closed: 'bg-gray-600/40 text-gray-400',
}
const PRIORITY_STYLE: Record<string, string> = {
  critical: 'text-red-400', high: 'text-orange-400', medium: 'text-yellow-400', low: 'text-gray-400',
}
const fmt = (d: string | null) => (d ? new Date(d).toLocaleString() : '—')
const label = (s: string) => s.replace(/_/g, ' ')

const FILTERS = [
  { k: '', label: 'All' },
  { k: 'open', label: 'Open' },
  { k: 'waiting_customer', label: 'Waiting' },
  { k: 'resolved', label: 'Resolved' },
  { k: 'closed', label: 'Closed' },
]

export default function MSPSupport() {
  const navigate = useNavigate()
  const [tickets, setTickets] = useState<Ticket[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState('')

  useEffect(() => { load(filter) }, [filter])

  const load = async (f: string) => {
    setLoading(true)
    try {
      const res = await api.get('/api/portal/msp/support/tickets', { params: f ? { status_filter: f } : {} })
      setTickets(res.data.tickets)
    } catch (e) {
      console.error('Failed to load MSP tickets:', e)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          <MessageSquare className="h-6 w-6 text-primary-500" /> Customer Tickets
        </h1>
        <p className="mt-1 text-sm text-gray-400">Support tickets from your customers. Reply directly, or escalate to Echonum.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        {FILTERS.map((f) => (
          <button key={f.k} onClick={() => setFilter(f.k)}
            className={`rounded-full px-3 py-1 text-sm ${filter === f.k ? 'bg-primary-500 text-white' : 'bg-dark-card text-gray-400 hover:text-white'}`}>
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex h-48 items-center justify-center"><Loader2 className="h-7 w-7 animate-spin text-primary-500" /></div>
      ) : tickets.length === 0 ? (
        <div className="rounded-lg border border-dark-border bg-dark-card p-8 text-center text-gray-400">No tickets{filter ? ` with status "${label(filter)}"` : ''}.</div>
      ) : (
        <div className="space-y-2">
          {tickets.map((t) => (
            <button key={t.id} onClick={() => navigate(`/msp/support/${t.id}`)}
              className="group flex w-full items-center gap-4 rounded-lg border border-dark-border bg-dark-card p-4 text-left transition-colors hover:border-primary-500/50">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate font-medium">{t.subject}</span>
                  {t.escalated && (
                    <span className="flex items-center gap-1 rounded bg-purple-500/15 px-1.5 py-0.5 text-[11px] text-purple-300">
                      <ArrowUpRight className="h-3 w-3" /> Escalated
                    </span>
                  )}
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
                  <span className="font-mono">{t.ticket_number}</span>
                  <span className="flex items-center gap-1"><Building2 className="h-3 w-3" />{t.customer.company_name || t.customer.email || 'Unknown'}</span>
                  <span className={PRIORITY_STYLE[t.priority] || ''}>{t.priority}</span>
                  <span>{t.comment_count} repl{t.comment_count === 1 ? 'y' : 'ies'}</span>
                  <span>updated {fmt(t.updated_at)}</span>
                </div>
              </div>
              <span className={`rounded-full px-2 py-0.5 text-xs capitalize ${STATUS_STYLE[t.status] || 'bg-gray-700 text-gray-300'}`}>{label(t.status)}</span>
              <ChevronRight className="h-5 w-5 flex-shrink-0 text-gray-600 group-hover:text-primary-500" />
            </button>
          ))}
        </div>
      )}
    </div>
  )
}
